import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line, Area, AreaChart } from 'recharts';
import { ArrowRight, CheckCircle, Mail, Star, Zap, Shield, Database, Cpu, Wifi, Users, TrendingUp, Sparkles, Award, Target, Calendar, Phone } from 'lucide-react';

const Industry40EvalTool = () => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', company: '', phone: '' });
  const [animationClass, setAnimationClass] = useState('animate-fade-in');

  const questions = [
    {
      id: 'automation',
      title: 'Production Automation',
      subtitle: 'How automated are your manufacturing processes?',
      icon: <Cpu className="w-8 h-8" />,
      gradient: 'from-blue-500 to-cyan-500',
      options: [
        { value: 1, label: 'Mostly Manual', description: 'Limited automation, manual processes' },
        { value: 2, label: 'Basic Automation', description: 'Some automated equipment' },
        { value: 3, label: 'Moderate Automation', description: 'Significant automation in key areas' },
        { value: 4, label: 'Highly Automated', description: 'Most processes automated' },
        { value: 5, label: 'Fully Autonomous', description: 'Smart, self-optimizing systems' }
      ]
    },
    {
      id: 'connectivity',
      title: 'Digital Connectivity',
      subtitle: 'How connected are your machines and systems?',
      icon: <Wifi className="w-8 h-8" />,
      gradient: 'from-purple-500 to-pink-500',
      options: [
        { value: 1, label: 'Isolated Systems', description: 'Standalone machines, no connectivity' },
        { value: 2, label: 'Basic Network', description: 'Some machines connected locally' },
        { value: 3, label: 'Integrated Network', description: 'Most systems networked' },
        { value: 4, label: 'IoT Enabled', description: 'Comprehensive IoT deployment' },
        { value: 5, label: 'Edge Computing', description: 'Real-time processing and connectivity' }
      ]
    },
    {
      id: 'data',
      title: 'Data & Analytics',
      subtitle: 'How do you collect and analyze operational data?',
      icon: <Database className="w-8 h-8" />,
      gradient: 'from-green-500 to-emerald-500',
      options: [
        { value: 1, label: 'Manual Reports', description: 'Paper-based or basic spreadsheets' },
        { value: 2, label: 'Basic Dashboards', description: 'Simple data visualization' },
        { value: 3, label: 'Real-time Monitoring', description: 'Live operational dashboards' },
        { value: 4, label: 'Predictive Analytics', description: 'AI-driven insights and predictions' },
        { value: 5, label: 'Autonomous Optimization', description: 'Self-learning systems' }
      ]
    },
    {
      id: 'security',
      title: 'Cybersecurity',
      subtitle: 'How secure are your industrial systems?',
      icon: <Shield className="w-8 h-8" />,
      gradient: 'from-red-500 to-orange-500',
      options: [
        { value: 1, label: 'Basic Protection', description: 'Standard antivirus and firewalls' },
        { value: 2, label: 'Network Security', description: 'Segmented networks, access control' },
        { value: 3, label: 'Advanced Monitoring', description: 'Threat detection and monitoring' },
        { value: 4, label: 'Zero Trust', description: 'Comprehensive security framework' },
        { value: 5, label: 'AI-Powered Defense', description: 'Autonomous threat response' }
      ]
    },
    {
      id: 'workforce',
      title: 'Digital Workforce',
      subtitle: 'How digitally skilled is your team?',
      icon: <Users className="w-8 h-8" />,
      gradient: 'from-indigo-500 to-blue-500',
      options: [
        { value: 1, label: 'Traditional Skills', description: 'Mainly mechanical and manual skills' },
        { value: 2, label: 'Basic Digital', description: 'Some computer literacy' },
        { value: 3, label: 'Digital Proficient', description: 'Good digital tool usage' },
        { value: 4, label: 'Data Savvy', description: 'Analytics and digital expertise' },
        { value: 5, label: 'Innovation Leaders', description: 'Digital transformation champions' }
      ]
    },
    {
      id: 'integration',
      title: 'System Integration',
      subtitle: 'How well do your systems work together?',
      icon: <Zap className="w-8 h-8" />,
      gradient: 'from-yellow-500 to-orange-500',
      options: [
        { value: 1, label: 'Siloed Systems', description: 'Isolated, standalone systems' },
        { value: 2, label: 'Basic Integration', description: 'Some system connections' },
        { value: 3, label: 'Coordinated Systems', description: 'Well-integrated operations' },
        { value: 4, label: 'Unified Platform', description: 'Comprehensive integration' },
        { value: 5, label: 'Ecosystem Hub', description: 'Full digital ecosystem' }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    setTimeout(() => {
      if (currentStep < questions.length - 1) {
        setAnimationClass('animate-slide-out');
        setTimeout(() => {
          setCurrentStep(prev => prev + 1);
          setAnimationClass('animate-slide-in');
        }, 200);
      } else {
        calculateResults();
      }
    }, 500);
  };

  const calculateResults = () => {
    const totalScore = Object.values(answers).reduce((sum, score) => sum + score, 0);
    const averageScore = totalScore / questions.length;
    const percentage = (averageScore / 5) * 100;
    
    let maturityLevel, levelColor, recommendations, industryData;
    
    if (percentage < 25) {
      maturityLevel = 'Digital Beginner';
      levelColor = 'text-red-400';
      recommendations = [
        'Implement basic IoT sensors for equipment monitoring',
        'Establish data collection and visualization systems',
        'Begin workforce digital literacy training',
        'Create cybersecurity baseline protection'
      ];
    } else if (percentage < 50) {
      maturityLevel = 'Digital Explorer';
      levelColor = 'text-orange-400';
      recommendations = [
        'Expand automation to critical manufacturing processes',
        'Integrate systems for seamless data flow',
        'Develop predictive maintenance capabilities',
        'Form cross-functional digital transformation teams'
      ];
    } else if (percentage < 75) {
      maturityLevel = 'Digital Innovator';
      levelColor = 'text-yellow-400';
      recommendations = [
        'Deploy AI-powered analytics for optimization',
        'Create comprehensive digital twin environments',
        'Implement autonomous quality control systems',
        'Lead industry best practice adoption'
      ];
    } else {
      maturityLevel = 'Digital Leader';
      levelColor = 'text-green-400';
      recommendations = [
        'Pioneer next-generation Industry 5.0 technologies',
        'Develop strategic technology partnerships',
        'Create proprietary AI and machine learning solutions',
        'Lead industry transformation through thought leadership'
      ];
    }

    // Generate industry benchmark data
    const industryAverage = Math.max(35, percentage - 15 + Math.random() * 10);
    const topPerformers = Math.min(95, percentage + 25 + Math.random() * 10);
    
    const benchmarkData = [
      { name: 'Your Score', value: Math.round(percentage), fill: '#3b82f6' },
      { name: 'Industry Average', value: Math.round(industryAverage), fill: '#64748b' },
      { name: 'Top 10%', value: Math.round(topPerformers), fill: '#10b981' }
    ];

    const categoryScores = questions.map(q => ({
      name: q.title.replace(' ', '\n'),
      yourScore: ((answers[q.id] || 0) / 5) * 100,
      industryAvg: Math.max(30, ((answers[q.id] || 0) / 5) * 100 - 15 + Math.random() * 20),
      topPerformers: Math.min(95, ((answers[q.id] || 0) / 5) * 100 + 20 + Math.random() * 15),
      fullMark: 100
    }));

    const maturityTrend = [
      { stage: 'Initial', yourPath: percentage >= 20 ? 100 : (percentage / 20) * 100 },
      { stage: 'Developing', yourPath: percentage >= 40 ? 100 : percentage > 20 ? ((percentage - 20) / 20) * 100 : 0 },
      { stage: 'Intermediate', yourPath: percentage >= 60 ? 100 : percentage > 40 ? ((percentage - 40) / 20) * 100 : 0 },
      { stage: 'Advanced', yourPath: percentage >= 80 ? 100 : percentage > 60 ? ((percentage - 60) / 20) * 100 : 0 },
      { stage: 'Leading', yourPath: percentage > 80 ? ((percentage - 80) / 20) * 100 : 0 }
    ];

    setResults({
      score: Math.round(percentage),
      level: maturityLevel,
      levelColor,
      recommendations,
      benchmarkData,
      categoryScores,
      maturityTrend,
      totalQuestions: questions.length,
      answeredQuestions: Object.keys(answers).length
    });
  };

  const resetAssessment = () => {
    setCurrentStep(-1);
    setAnswers({});
    setResults(null);
    setShowLeadForm(false);
    setLeadData({ name: '', email: '', company: '', phone: '' });
    setAnimationClass('animate-fade-in');
  };

  const renderWelcome = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl mx-auto text-center text-white">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-6">
            <Sparkles className="w-10 h-10" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Industry 4.0 Readiness
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8">
            Benchmark your digital transformation against industry leaders
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-12 max-w-2xl mx-auto">
          {[
            { icon: <Cpu className="w-6 h-6" />, label: 'Automation' },
            { icon: <Database className="w-6 h-6" />, label: 'Analytics' },
            { icon: <Shield className="w-6 h-6" />, label: 'Security' },
            { icon: <Wifi className="w-6 h-6" />, label: 'Connectivity' },
            { icon: <Users className="w-6 h-6" />, label: 'Workforce' },
            { icon: <Zap className="w-6 h-6" />, label: 'Integration' }
          ].map((item, index) => (
            <div key={index} className="flex flex-col items-center p-4 bg-white/10 rounded-lg backdrop-blur-sm">
              <div className="text-blue-400 mb-2">{item.icon}</div>
              <span className="text-sm font-medium">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8 max-w-md mx-auto">
          <h3 className="font-semibold mb-4">What You'll Get:</h3>
          <div className="space-y-2 text-sm text-left">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span>Industry benchmark comparison</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span>Personalized transformation roadmap</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span>Free consultation opportunity</span>
            </div>
          </div>
        </div>

        <button
          onClick={() => setCurrentStep(0)}
          className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          Start Free Assessment
          <ArrowRight className="inline-block ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );

  const renderQuestion = () => {
    const question = questions[currentStep];
    const progress = ((currentStep + 1) / questions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-4xl mx-auto w-full">
          <div className="mb-8">
            <div className="flex justify-between text-white mb-2">
              <span className="text-sm font-medium">Question {currentStep + 1} of {questions.length}</span>
              <span className="text-sm font-medium">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          <div className={`${animationClass} transition-all duration-300`}>
            <div className="text-center mb-12">
              <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${question.gradient} rounded-full mb-6`}>
                <div className="text-white">{question.icon}</div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{question.title}</h2>
              <p className="text-xl text-gray-300">{question.subtitle}</p>
            </div>

            <div className="grid gap-4 max-w-3xl mx-auto">
              {question.options.map((option, index) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(question.id, option.value)}
                  className="group relative p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300 text-left hover:scale-[1.02] hover:shadow-xl"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center mb-2">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${question.gradient} flex items-center justify-center text-white font-bold mr-3`}>
                          {option.value}
                        </div>
                        <h3 className="text-lg font-semibold text-white">{option.label}</h3>
                      </div>
                      <p className="text-gray-300 text-sm">{option.description}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Your Industry 4.0 Benchmark Results
          </h1>
          <p className="text-xl text-gray-300">See how you compare to industry standards</p>
        </div>

        {/* Main Score */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white shadow-2xl h-full flex flex-col justify-center">
              <div className="text-6xl md:text-7xl font-bold mb-4">{results.score}%</div>
              <div className={`text-2xl md:text-3xl font-semibold mb-4 ${results.levelColor}`}>
                {results.level}
              </div>
              <div className="flex justify-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star 
                    key={star} 
                    className={`w-6 h-6 ${star <= Math.ceil(results.score / 20) ? 'text-yellow-400 fill-current' : 'text-gray-400'}`} 
                  />
                ))}
              </div>
              <p className="text-lg opacity-90">Digital Maturity Level</p>
            </div>
          </div>

          {/* Industry Benchmark */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 h-full">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Target className="w-6 h-6 text-blue-400" />
                Industry Benchmark Comparison
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={results.benchmarkData}>
                  <XAxis dataKey="name" tick={{ fill: '#d1d5db' }} />
                  <YAxis tick={{ fill: '#d1d5db' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0,0,0,0.8)', 
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white'
                    }} 
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Detailed Category Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h3 className="text-xl font-semibold text-white mb-6">Category Radar Analysis</h3>
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={results.categoryScores}>
                <PolarGrid stroke="#374151" />
                <PolarAngleAxis dataKey="name" tick={{ fill: '#d1d5db', fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} />
                <Radar name="Your Score" dataKey="yourScore" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} strokeWidth={3} />
                <Radar name="Industry Avg" dataKey="industryAvg" stroke="#64748b" fill="transparent" strokeWidth={2} strokeDasharray="5 5" />
                <Radar name="Top 10%" dataKey="topPerformers" stroke="#10b981" fill="transparent" strokeWidth={2} strokeDasharray="3 3" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white'
                  }} 
                />
              </RadarChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-300">Your Score</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-1 bg-gray-500"></div>
                <span className="text-gray-300">Industry Avg</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-1 bg-green-500"></div>
                <span className="text-gray-300">Top 10%</span>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h3 className="text-xl font-semibold text-white mb-6">Maturity Journey Progress</h3>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={results.maturityTrend}>
                <XAxis dataKey="stage" tick={{ fill: '#d1d5db', fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fill: '#d1d5db' }} />
                <Area 
                  type="monotone" 
                  dataKey="yourPath" 
                  stroke="#3b82f6" 
                  fill="url(#colorGradient)" 
                  strokeWidth={3}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white'
                  }} 
                />
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detailed Category Breakdown */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 mb-8">
          <h3 className="text-xl font-semibold text-white mb-6">Detailed Category Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {questions.map(question => {
              const score = ((answers[question.id] || 0) / 5) * 100;
              const categoryData = results.categoryScores.find(c => c.name.includes(question.title.split(' ')[0]));
              return (
                <div key={question.id} className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${question.gradient} text-white`}>
                      {React.cloneElement(question.icon, { className: "w-5 h-5" })}
                    </div>
                    <h4 className="font-semibold text-white">{question.title}</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-300">Your Score</span>
                      <span className="font-bold text-white">{Math.round(score)}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className={`bg-gradient-to-r ${question.gradient} h-2 rounded-full transition-all duration-1000`}
                        style={{ width: `${score}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-400 space-y-1">
                      <div className="flex justify-between">
                        <span>Industry Avg:</span>
                        <span>{Math.round(categoryData?.industryAvg || 0)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Top 10%:</span>
                        <span>{Math.round(categoryData?.topPerformers || 0)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 mb-8">
          <h3 className="text-2xl font-semibold text-white mb-6">Your Personalized Transformation Roadmap</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.recommendations.map((rec, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-full p-1 mt-1">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <span className="text-gray-200">{rec}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Lead Generation CTA */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8 text-center text-white shadow-2xl mb-8">
          <div className="flex items-center justify-center mb-4">
            <Award className="w-12 h-12 text-yellow-400" />
          </div>
          <h3 className="text-3xl font-bold mb-4">Ready to Accelerate Your Digital Transformation?</h3>
          <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
            Get a personalized 30-minute consultation with our Industry 4.0 experts. We'll analyze your results and create a custom implementation strategy for your organization.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold">500+</div>
              <div className="text-sm opacity-90">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">98%</div>
              <div className="text-sm opacity-90">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">15+</div>
              <div className="text-sm opacity-90">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">$2.5M</div>
              <div className="text-sm opacity-90">Avg ROI Generated</div>
            </div>
          </div>

          {!showLeadForm ? (
            <button 
              onClick={() => setShowLeadForm(true)}
              className="px-8 py-4 bg-white text-blue-600 rounded-full font-semibold hover:bg-gray-100 transition-colors text-lg shadow-lg"
            >
              Get My Free Consultation
            </button>
          ) : (
            <div className="max-w-md mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h4 className="text-xl font-semibold mb-4">Schedule Your Free Consultation</h4>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Full Name"
                  value={leadData.name}
                  onChange={(e) => setLeadData(prev => ({...prev, name: e.target.value}))}
                  className="w-full p-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-white/50"
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={leadData.email}
                  onChange={(e) => setLeadData(prev => ({...prev, email: e.target.value}))}
                  className="w-full p-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-white/50"
                />
                <input
                  type="text"
                  placeholder="Company Name"
                  value={leadData.company}
                  onChange={(e) => setLeadData(prev => ({...prev, company: e.target.value}))}
                  className="w-full p-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-white/50"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={leadData.phone}
                  onChange={(e) => setLeadData(prev => ({...prev, phone: e.target.value}))}
                  className="w-full p-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-white/50"
                />
                <button
                  className="w-full px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                  disabled={!leadData.name || !leadData.email}
                >
                  <Calendar className="w-4 h-4" />
                  Schedule My Consultation
                </button>
              </div>
              <p className="text-xs text-gray-300 mt-4">
                We'll contact you within 24 hours to schedule your free consultation.
              </p>
            </div>
          )}
        </div>

        <div className="text-center">
          <button 
            onClick={resetAssessment}
            className="px-6 py-3 border border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors"
          >
            Take Assessment Again
          </button>
        </div>
      </div>
    </div>
  );

  // Entry point - show welcome screen initially
  if (currentStep === -1) {
    return renderWelcome();
  }

  // Show results after completing assessment
  if (results) {
    return renderResults();
  }

  // Show questions
  if (currentStep < questions.length) {
    return renderQuestion();
  }

  // Default to welcome screen
  return renderWelcome();
};

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(50px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideOut {
    from { opacity: 1; transform: translateX(0); }
    to { opacity: 0; transform: translateX(-50px); }
  }
  .animate-fade-in {
    animation: fadeIn 0.5s ease-out;
  }
  .animate-slide-in {
    animation: slideIn 0.3s ease-out;
  }
  .animate-slide-out {
    animation: slideOut 0.2s ease-in;
  }
`;
document.head.appendChild(style);

export default Industry40EvalTool;